import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMenu } from '../context/MenuContext';
import MenuShareButton from './MenuShareButton';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCalendar,
  FiUsers,
  FiClock,
  FiArrowRight,
  FiStar,
  FiCoffee,
  FiLock,
  FiAlertCircle,
  FiInfo,
  FiGift,
  FiCheckCircle
} = FiIcons;

const MenuOverview = () => {
  const navigate = useNavigate();
  const {
    weeklyMenus,
    nextWeeklyMenus,
    isWeeklyMenuOrderable,
    getOrderableDaysForWeeklyMenu,
    getFormattedServiceTime // ✅ NEUE FUNKTION
  } = useMenu();

  const handleWeeklyRegistration = () => {
    navigate('/anmeldung/woche');
  };

  const handleAdminLogin = () => {
    navigate('/admin/login');
  };

  // KORRIGIERTE SORTIERUNG: Sortiere die verfügbaren Menüs nach der richtigen Reihenfolge
  const getSortedAvailableMenus = () => {
    const dayOrder = { 'dienstag': 1, 'mittwoch': 2, 'donnerstag': 3, 'freitag': 4 };

    return Object.entries(weeklyMenus)
      .filter(([_, menu]) => menu.isOrderable)
      .sort(([dayA], [dayB]) => {
        const orderA = dayOrder[dayA?.toLowerCase()] || 999;
        const orderB = dayOrder[dayB?.toLowerCase()] || 999;
        return orderA - orderB;
      });
  };

  const availableMenus = getSortedAvailableMenus();
  const hasAvailableMenus = availableMenus.length > 0;

  // Prüfen ob Wochenmenü bestellbar ist
  const canOrderWeeklyMenu = isWeeklyMenuOrderable(false);

  // Bestellbare Tage für das Wochenmenü
  const orderableDays = getOrderableDaysForWeeklyMenu(false);

  // KORRIGIERTE SORTIERUNG: Sortiere die nächste Woche Menüs nach der richtigen Reihenfolge
  const getSortedNextWeekMenus = () => {
    const dayOrder = { 'dienstag': 1, 'mittwoch': 2, 'donnerstag': 3, 'freitag': 4 };

    return Object.entries(nextWeeklyMenus).sort(([dayA], [dayB]) => {
      const orderA = dayOrder[dayA?.toLowerCase()] || 999;
      const orderB = dayOrder[dayB?.toLowerCase()] || 999;
      return orderA - orderB;
    });
  };

  // Nächste Woche Menüs
  const nextWeekAvailable = Object.entries(nextWeeklyMenus).length > 0;
  const nextWeekMenus = getSortedNextWeekMenus();
  const canOrderNextWeekMenu = isWeeklyMenuOrderable(true);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white shadow-lg"
      >
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-800 mb-2">
              Wöchentliches Mittagsmenü
            </h1>
            <p className="text-lg text-gray-600">
              Geniessen Sie unsere täglich frisch zubereiteten Menüs
            </p>
          </div>
        </div>
      </motion.header>

      {/* Prominent Benefits Banner */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-r from-primary-500 to-primary-600 text-white py-8"
      >
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-center text-2xl font-bold mb-6">Ihre Vorteile bei uns</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white/20 rounded-xl p-5 text-center backdrop-blur-sm">
              <div className="bg-white/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCoffee} className="text-2xl" />
              </div>
              <h3 className="font-bold text-xl mb-2">Gratis Kaffee</h3>
              <p>Ab 4 Personen erhält jede Person einen kostenlosen Kaffee</p>
            </div>
            <div className="bg-white/20 rounded-xl p-5 text-center backdrop-blur-sm">
              <div className="bg-white/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiStar} className="text-2xl" />
              </div>
              <h3 className="font-bold text-xl mb-2">Gratis Dessert</h3>
              <p>Ab 6 Personen gibt es ein kostenloses Dessert für alle</p>
            </div>
            <div className="bg-white/20 rounded-xl p-5 text-center backdrop-blur-sm">
              <div className="bg-white/30 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiGift} className="text-2xl" />
              </div>
              <h3 className="font-bold text-xl mb-2">Wochenbonus</h3>
              <p>Bei Wochenbestellung erhalten Sie ein kostenloses Dessert</p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Weekly Registration */}
      {canOrderWeeklyMenu && (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-6xl mx-auto px-4 py-8"
        >
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-2xl p-8 text-white shadow-xl">
            <div className="flex flex-col md:flex-row items-center">
              <div className="md:w-2/3 mb-6 md:mb-0 md:pr-6">
                <h2 className="text-3xl font-bold mb-4">Für die ganze Woche anmelden</h2>
                <p className="text-xl mb-2">
                  Bestellbare Tage: {orderableDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')}
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 text-white/80" />
                    <span>Sparen Sie Zeit mit nur einer Anmeldung</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 text-white/80" />
                    <span>Erhalten Sie ein kostenloses Dessert</span>
                  </div>
                  <div className="flex items-center">
                    <SafeIcon icon={FiCheckCircle} className="mr-2 text-white/80" />
                    <span>Planen Sie Ihre Woche im Voraus</span>
                  </div>
                </div>
              </div>
              <div className="md:w-1/3 flex justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleWeeklyRegistration}
                  className="bg-white text-green-600 px-8 py-4 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center space-x-2"
                >
                  <span>Wochenanmeldung</span>
                  <SafeIcon icon={FiArrowRight} />
                </motion.button>
              </div>
            </div>
          </div>
        </motion.section>
      )}

      {/* Aktuelle Tagesmenüs */}
      <section className="max-w-6xl mx-auto px-4 py-8">
        <h2 className="text-3xl font-bold text-center text-gray-800 mb-6">
          Unsere Tagesmenüs
        </h2>

        {/* Hinweis bei keinen verfügbaren Menüs */}
        {!hasAvailableMenus && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8 text-center"
          >
            <div className="flex items-center justify-center mb-4">
              <SafeIcon icon={FiInfo} className="text-3xl text-amber-500 mr-3" />
              <h3 className="text-xl font-semibold text-amber-700">Keine aktuellen Menüs verfügbar</h3>
            </div>
            <p className="text-amber-700 mb-4">
              Für diese Woche sind keine weiteren Menüs mehr verfügbar oder bestellbar.
            </p>
            {nextWeekAvailable && (
              <div className="mt-4">
                <p className="text-amber-800 font-medium mb-3">Schauen Sie sich unsere Menüs für nächste Woche an:</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/naechste-woche')}
                  className="bg-amber-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-amber-700 transition-colors flex items-center space-x-2 mx-auto"
                >
                  <span>Menüs nächste Woche</span>
                  <SafeIcon icon={FiArrowRight} />
                </motion.button>
              </div>
            )}
          </motion.div>
        )}

        {/* Aktuelle Menüs Anzeige - KORREKT SORTIERT */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {hasAvailableMenus &&
            availableMenus.map(([key, menu], index) => (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-300"
              >
                <div className="relative">
                  <img src={menu.image} alt={menu.name} className="w-full h-48 object-cover" />
                  <div className="absolute top-4 left-4 bg-primary-500 text-white px-3 py-1 rounded-full font-semibold">
                    {menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}
                    {menu.formattedDate ? ` - ${menu.formattedDate}` : ''}
                  </div>
                  {menu.isToday && (
                    <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full font-semibold">
                      Heute
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{menu.name}</h3>
                  <p className="text-gray-600 mb-4 text-sm leading-relaxed">{menu.description}</p>
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-2xl font-bold text-primary-600">CHF {menu.price.toFixed(2)}</span>
                    <div className="flex items-center text-gray-500 text-sm">
                      <SafeIcon icon={FiClock} className="mr-1" />
                      {/* ✅ VERWENDE DYNAMISCHE SERVICE-ZEIT */}
                      <span>{getFormattedServiceTime()}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-2">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate(`/anmeldung/${key}`)}
                      className="w-full bg-primary-500 text-white py-3 rounded-xl font-semibold hover:bg-primary-600 transition-colors duration-300 flex items-center justify-center space-x-2"
                    >
                      <span>Jetzt anmelden</span>
                      <SafeIcon icon={FiArrowRight} />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => navigate(`/anmeldung/flexible/${key}`)}
                      className="w-full bg-green-500 text-white py-2 rounded-xl font-medium hover:bg-green-600 transition-colors duration-300 flex items-center justify-center space-x-2"
                    >
                      <SafeIcon icon={FiCalendar} />
                      <span>5 Tage ab hier buchen</span>
                    </motion.button>
                  </div>
                  {/* ✅ SHARING-BUTTONS */}
                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
                    <span className="text-sm text-gray-500">Teilen:</span>
                    <MenuShareButton menu={menu} isNextWeek={false} />
                  </div>
                </div>
              </motion.div>
            ))}
        </div>
      </section>

      {/* Preview der nächsten Woche Menüs - KORREKT SORTIERT */}
      {nextWeekAvailable && (
        <section className="bg-blue-50 py-12 mt-8 mb-12">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex flex-col md:flex-row justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">
                Vorschau: Menüs nächste Woche
              </h2>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate('/naechste-woche')}
                className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center space-x-2"
              >
                <span>Alle Menüs anzeigen</span>
                <SafeIcon icon={FiArrowRight} />
              </motion.button>
            </div>

            {/* Carousel-Style für die nächste Woche - KORREKT SORTIERT */}
            <div className="overflow-x-auto pb-4">
              <div className="flex gap-6" style={{ minWidth: 'max-content' }}>
                {nextWeekMenus.slice(0, 4).map(([key, menu], index) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="bg-white rounded-2xl shadow-lg overflow-hidden w-72 flex-shrink-0"
                  >
                    <div className="relative">
                      <img src={menu.image} alt={menu.name} className="w-full h-40 object-cover" />
                      <div className="absolute top-4 left-4 bg-blue-500 text-white px-3 py-1 rounded-full font-semibold">
                        {menu.day.charAt(0).toUpperCase() + menu.day.slice(1)}
                        {menu.formattedDate ? ` - ${menu.formattedDate}` : ''}
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-lg font-bold text-gray-800 truncate">{menu.name}</h3>
                      <p className="text-gray-600 text-sm line-clamp-2 h-10 mb-4">{menu.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-primary-600">CHF {menu.price.toFixed(2)}</span>
                        <div className="flex items-center space-x-2">
                          <MenuShareButton menu={menu} isNextWeek={true} />
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => navigate(`/anmeldung/next-${key}`)}
                            className="bg-blue-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-600 transition-colors flex items-center"
                          >
                            <span>Vorbestellen</span>
                            <SafeIcon icon={FiArrowRight} className="ml-1 text-xs" />
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Wochenbestellung für nächste Woche */}
            {canOrderNextWeekMenu && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-8 bg-blue-100 border border-blue-200 rounded-xl p-6 text-center"
              >
                <h3 className="text-xl font-bold text-blue-800 mb-3">Für die ganze nächste Woche anmelden</h3>
                <p className="text-blue-700 mb-4">
                  Planen Sie im Voraus und sichern Sie sich Ihr kostenloses Dessert!
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/anmeldung/next-woche')}
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors inline-flex items-center space-x-2"
                >
                  <span>Nächste Woche komplett buchen</span>
                  <SafeIcon icon={FiCalendar} />
                </motion.button>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Info Section */}
      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
        className="bg-gray-100 py-12"
      >
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Anmeldung ganz einfach</h3>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex flex-col items-center">
              <div className="bg-primary-100 p-4 rounded-full mb-4">
                <SafeIcon icon={FiCalendar} className="text-primary-600 text-2xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Menü wählen</h4>
              <p className="text-gray-600 text-sm">
                Wählen Sie Ihr gewünschtes Tagesmenü oder melden Sie sich für die ganze Woche an
              </p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-primary-100 p-4 rounded-full mb-4">
                <SafeIcon icon={FiUsers} className="text-primary-600 text-2xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Daten eingeben</h4>
              <p className="text-gray-600 text-sm">Geben Sie Ihre Kontaktdaten und Anzahl Personen ein</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-primary-100 p-4 rounded-full mb-4">
                <SafeIcon icon={FiCoffee} className="text-primary-600 text-2xl" />
              </div>
              <h4 className="font-semibold text-gray-800 mb-2">Geniessen</h4>
              <p className="text-gray-600 text-sm">
                {/* ✅ VERWENDE DYNAMISCHE SERVICE-ZEIT */}
                Kommen Sie um {getFormattedServiceTime()} und geniessen Sie Ihr Menü
              </p>
            </div>
          </div>
        </div>
      </motion.section>

      {/* Admin Login Button (Footer) */}
      <div className="bg-gray-200 py-4 mt-12">
        <div className="max-w-6xl mx-auto px-4 flex justify-center">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleAdminLogin}
            className="flex items-center space-x-2 text-gray-600 hover:text-primary-600 transition-colors"
          >
            <SafeIcon icon={FiLock} className="text-sm" />
            <span className="text-sm">Admin-Bereich</span>
          </motion.button>
        </div>
      </div>
    </div>
  );
};

export default MenuOverview;